
import React from 'react';
import { SimulationConfig } from '@/types';
import { SectionLabel } from './SectionLabel';
import { SmartInput } from './SmartInput';
import { ControlGroup } from './ControlGroup';
import { InfoPopup } from './InfoPopup';

interface TitleFooterSettingsSectionProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
}

const ColorInput = ({ label, value, onChange, darkMode, settingKey }: { label: string, value: string, onChange: (v: string) => void, darkMode: boolean, settingKey?: string }) => (
  <ControlGroup label={label} settingKey={settingKey} darkMode={darkMode}>
      <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full border border-white/20 shadow-sm" style={{ backgroundColor: value }} />
          <input type="color" value={value} onChange={e => onChange(e.target.value)} className="flex-1 h-8 opacity-0 absolute cursor-pointer" />
          <input type="text" value={value} onChange={e => onChange(e.target.value)} className={`flex-1 px-2 py-1 text-[9px] font-mono border rounded-lg ${darkMode ? 'bg-neutral-800 border-neutral-700' : 'bg-white border-neutral-200'}`} />
      </div>
  </ControlGroup>
);

const FONT_FAMILIES = [
  'Inter', 'Space Grotesk', 'Outfit', 'Playfair Display', 'JetBrains Mono', 'Fira Code', 'Roboto', 'Montserrat', 'Open Sans', 'Lato', 'Poppins', 'Raleway', 'Ubuntu', 'Oswald', 'Merriweather', 'Shadows Into Light', 'Permanent Marker', 'Dancing Script'
];

const FONT_WEIGHTS = [
  { label: 'Thin', value: '100' },
  { label: 'Light', value: '300' },
  { label: 'Normal', value: '400' },
  { label: 'Medium', value: '500' },
  { label: 'Semi-Bold', value: '600' },
  { label: 'Bold', value: '700' },
  { label: 'Extra-Bold', value: '800' },
  { label: 'Black', value: '900' }
];

export const TitleFooterSettingsSection: React.FC<TitleFooterSettingsSectionProps> = ({ config, setConfig }) => {
  return (
    <div className="space-y-6">
      <div>
        <SectionLabel icon="fa-heading" darkMode={config.darkMode}>Title Settings</SectionLabel>
        <div className="space-y-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
                <label className={`text-[10px] font-black uppercase tracking-widest ${config.darkMode ? 'text-neutral-500' : 'text-neutral-400'}`}>Title Text</label>
                <InfoPopup settingKey="titleText" darkMode={config.darkMode} />
            </div>
            <input 
              type="text" 
              value={config.titleText} 
              onChange={(e) => setConfig(prev => ({ ...prev, titleText: e.target.value }))} 
              className={`w-full px-3 py-2 border rounded-xl text-xs font-bold outline-none transition-all ${config.darkMode ? 'bg-neutral-800 border-neutral-700 text-white focus:border-blue-500' : 'bg-neutral-50 border-neutral-200 text-neutral-800 focus:border-blue-400'}`} 
              placeholder="Enter title text..." 
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                  <label className={`text-[10px] font-black uppercase tracking-widest ${config.darkMode ? 'text-neutral-500' : 'text-neutral-400'}`}>Font Family</label>
                  <InfoPopup settingKey="titleFontFamily" darkMode={config.darkMode} />
              </div>
              <select 
                value={config.titleFontFamily} 
                onChange={(e) => setConfig(prev => ({ ...prev, titleFontFamily: e.target.value }))}
                className={`w-full px-3 py-2 border rounded-xl text-xs font-bold outline-none transition-all ${config.darkMode ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-neutral-50 border-neutral-200 text-neutral-800'}`}
              >
                {FONT_FAMILIES.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                  <label className={`text-[10px] font-black uppercase tracking-widest ${config.darkMode ? 'text-neutral-500' : 'text-neutral-400'}`}>Font Weight</label>
                  <InfoPopup settingKey="titleFontWeight" darkMode={config.darkMode} />
              </div>
              <select 
                value={config.titleFontWeight} 
                onChange={(e) => setConfig(prev => ({ ...prev, titleFontWeight: e.target.value }))}
                className={`w-full px-3 py-2 border rounded-xl text-xs font-bold outline-none transition-all ${config.darkMode ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-neutral-50 border-neutral-200 text-neutral-800'}`}
              >
                {FONT_WEIGHTS.map(w => <option key={w.value} value={w.value}>{w.label}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <SmartInput label="Font Size" settingKey="titleFontSize" min={10} max={200} value={config.titleFontSize} onChange={(v: number) => setConfig(p => ({...p, titleFontSize: v}))} darkMode={config.darkMode} />
            <SmartInput label="Vertical Offset" settingKey="titleOffsetY" min={0} max={1000} value={config.titleOffsetY} onChange={(v: number) => setConfig(p => ({...p, titleOffsetY: v}))} darkMode={config.darkMode} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <ControlGroup label="Bold Text" settingKey="titleBold" darkMode={config.darkMode}>
                <div 
                    onClick={() => setConfig(p => ({ ...p, titleBold: !p.titleBold }))}
                    className={`w-10 h-5 rounded-full p-1 cursor-pointer transition-all ${config.titleBold ? 'bg-blue-500' : (config.darkMode ? 'bg-neutral-700' : 'bg-neutral-200')}`}
                >
                    <div className={`w-3 h-3 rounded-full bg-white transition-all transform ${config.titleBold ? 'translate-x-5' : 'translate-x-0'}`} />
                </div>
            </ControlGroup>
            <ColorInput label="Text Color" settingKey="titleColor" value={config.titleColor} onChange={(v: string) => setConfig(p => ({...p, titleColor: v}))} darkMode={config.darkMode} />
          </div>
        </div>
      </div>

      <div className="pt-4 border-t border-neutral-200 dark:border-neutral-800">
        <SectionLabel icon="fa-paragraph" darkMode={config.darkMode}>Footer Settings</SectionLabel>
        <div className="space-y-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
                <label className={`text-[10px] font-black uppercase tracking-widest ${config.darkMode ? 'text-neutral-500' : 'text-neutral-400'}`}>Footer Text</label>
                <InfoPopup settingKey="footerText" darkMode={config.darkMode} />
            </div>
            <input 
              type="text" 
              value={config.footerText} 
              onChange={(e) => setConfig(prev => ({ ...prev, footerText: e.target.value }))} 
              className={`w-full px-3 py-2 border rounded-xl text-xs font-bold outline-none transition-all ${config.darkMode ? 'bg-neutral-800 border-neutral-700 text-white focus:border-blue-500' : 'bg-neutral-50 border-neutral-200 text-neutral-800 focus:border-blue-400'}`} 
              placeholder="Enter footer text..." 
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                  <label className={`text-[10px] font-black uppercase tracking-widest ${config.darkMode ? 'text-neutral-500' : 'text-neutral-400'}`}>Font Family</label>
                  <InfoPopup settingKey="footerFontFamily" darkMode={config.darkMode} />
              </div>
              <select 
                value={config.footerFontFamily} 
                onChange={(e) => setConfig(prev => ({ ...prev, footerFontFamily: e.target.value }))}
                className={`w-full px-3 py-2 border rounded-xl text-xs font-bold outline-none transition-all ${config.darkMode ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-neutral-50 border-neutral-200 text-neutral-800'}`}
              >
                {FONT_FAMILIES.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                  <label className={`text-[10px] font-black uppercase tracking-widest ${config.darkMode ? 'text-neutral-500' : 'text-neutral-400'}`}>Font Weight</label>
                  <InfoPopup settingKey="footerFontWeight" darkMode={config.darkMode} />
              </div>
              <select 
                value={config.footerFontWeight} 
                onChange={(e) => setConfig(prev => ({ ...prev, footerFontWeight: e.target.value }))}
                className={`w-full px-3 py-2 border rounded-xl text-xs font-bold outline-none transition-all ${config.darkMode ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-neutral-50 border-neutral-200 text-neutral-800'}`}
              >
                {FONT_WEIGHTS.map(w => <option key={w.value} value={w.value}>{w.label}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <SmartInput label="Font Size" settingKey="footerFontSize" min={8} max={100} value={config.footerFontSize} onChange={(v: number) => setConfig(p => ({...p, footerFontSize: v}))} darkMode={config.darkMode} />
            <SmartInput label="Vertical Offset" settingKey="footerOffsetY" min={0} max={1000} value={config.footerOffsetY} onChange={(v: number) => setConfig(p => ({...p, footerOffsetY: v}))} darkMode={config.darkMode} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <ControlGroup label="Bold Text" settingKey="footerBold" darkMode={config.darkMode}>
                <div 
                    onClick={() => setConfig(p => ({ ...p, footerBold: !p.footerBold }))}
                    className={`w-10 h-5 rounded-full p-1 cursor-pointer transition-all ${config.footerBold ? 'bg-blue-500' : (config.darkMode ? 'bg-neutral-700' : 'bg-neutral-200')}`}
                >
                    <div className={`w-3 h-3 rounded-full bg-white transition-all transform ${config.footerBold ? 'translate-x-5' : 'translate-x-0'}`} />
                </div>
            </ControlGroup>
            <ColorInput label="Text Color" settingKey="footerTextColor" value={config.footerTextColor} onChange={(v: string) => setConfig(p => ({...p, footerTextColor: v}))} darkMode={config.darkMode} />
          </div>
        </div>
      </div>
    </div>
  );
};
